import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_CATEGORY_LABELS,
} from '@claimup/shared/constants/expedient-documents';
import { classifyDocument } from '../../services/s3Service';

/**
 * Qué es un documento y si se le puede enviar al deudor.
 *
 * **Solo sale en expedientes AR.** En recobro y judicial los documentos no se le
 * mandan al deudor desde aquí, así que clasificarlos no significaría nada — y el
 * backend devuelve 400 si se intenta.
 *
 * Son dos preguntas distintas a propósito: *qué es* y *quién puede verlo*. Un
 * contrato puede no ser enviable, y con un solo desplegable («otros público /
 * otros privado») no habría forma de decirlo.
 *
 * **Se guarda al momento**, sin botón. Son dos campos y pulsar «guardar» por
 * cada documento de una lista sería un peaje; a cambio, si el servidor falla se
 * deshace lo que se acaba de ver y se dice por qué — porque aquí una mentira en
 * pantalla significaría creer que algo no sale cuando sí.
 */
const ClasificacionDocumento = ({ expedientId, documento }) => {
  const [categoria, setCategoria] = useState(documento.document_category || '');
  const [enviable, setEnviable] = useState(Boolean(documento.shareable_with_debtor));
  const [guardando, setGuardando] = useState(false);

  const guardar = async (patch, deshacer) => {
    setGuardando(true);
    try {
      await classifyDocument(expedientId, documento._id, patch);
    } catch (error) {
      deshacer();
      toast.error(error?.response?.data?.error || 'No se pudo guardar la clasificación');
    } finally {
      setGuardando(false);
    }
  };

  const cambiarCategoria = valor => {
    const anterior = categoria;
    setCategoria(valor);
    guardar({ document_category: valor || null }, () => setCategoria(anterior));
  };

  const cambiarEnviable = valor => {
    setEnviable(valor);
    guardar({ shareable_with_debtor: valor }, () => setEnviable(!valor));
  };

  return (
    <div className='flex flex-wrap items-center gap-3 mt-1'>
      <select
        value={categoria}
        disabled={guardando}
        onChange={e => cambiarCategoria(e.target.value)}
        aria-label={`Tipo de ${documento.file_name}`}
        className='rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 disabled:opacity-50'
      >
        <option value=''>Sin clasificar</option>
        {DOCUMENT_CATEGORIES.map(c => (
          <option key={c} value={c}>
            {DOCUMENT_CATEGORY_LABELS[c]}
          </option>
        ))}
      </select>

      <label className='flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer'>
        <input
          type='checkbox'
          checked={enviable}
          disabled={guardando}
          onChange={e => cambiarEnviable(e.target.checked)}
          aria-label={`Se le puede enviar al deudor: ${documento.file_name}`}
          className='rounded-lg border-gray-300'
        />
        Se le puede enviar al deudor
      </label>
    </div>
  );
};

export default ClasificacionDocumento;
